import React, { ReactNode } from 'react';
import { S } from './ControlStats.style';
import { GetClientOverviewStatisticsDto } from '@ansvar-sakerhet/api-client';
import { RenderCounterProps } from 'react-countup/build/types';
import CountUp from 'react-countup';

type Props = {
  stats: GetClientOverviewStatisticsDto | undefined;
  name: string;
  project?: boolean;
};

export const ControlStats = ({ stats, name, project }: Props) => {
  if (!stats) return null;

  // Negative when fixes were registered on controls whose statistics are not generated yet.
  const openAdjusted = stats.deviations.open < 0;

  return (
    <S.StatsInner>
      <S.H6>Arbetsplatskontroller</S.H6>
      <S.Margin $bottom="m" />

      <CountUpPreset value={stats.controls.amount}>
        {({ countUpRef }) => (
          <div style={{ display: 'flex' }}>
            <S.H3>
              <span ref={countUpRef} />{' '}
              {stats.controls.amount === 1 ? 'kontroll' : 'kontroller'}
            </S.H3>
          </div>
        )}
      </CountUpPreset>
      <S.Margin $top="l" />
      {/* Ny tabell */}
      <S.Margin $top="m" />
      <S.Table>
        <S.THead>
          <tr>
            <S.THeadCell>Mest förekommande avvikelser</S.THeadCell>
            <S.THeadCell>{name}</S.THeadCell>
            <S.THeadCell>Snitt i landet</S.THeadCell>
          </tr>
        </S.THead>
        <S.TBody>
          <S.TBodyRow>
            <S.TBodyCell style={{ paddingLeft: 5 }}>
              <S.H6 style={{ fontWeight: 400, fontSize: 14 }}>
                Kontrollerade, ej inloggade i EPL
              </S.H6>
            </S.TBodyCell>
            <S.TBodyCell>
              {project
                ? stats.workControl.project.percentageOfAdditionalPersonnel ??
                  ' - '
                : stats.workControl.client.percentageOfAdditionalPersonnel}
              %
            </S.TBodyCell>
            <S.TBodyCell>
              {stats.workControl.all.percentageOfAdditionalPersonnel}%
            </S.TBodyCell>
          </S.TBodyRow>
          <S.TBodyRow>
            <S.TBodyCell style={{ paddingLeft: 5 }}>
              <S.H6 style={{ fontWeight: 400, fontSize: 14 }}>
                Avvikelser mot ID06 regelverk
              </S.H6>
            </S.TBodyCell>
            <S.TBodyCell>
              {project
                ? stats.workControl.project
                    .percentageOfPersonnelWithIdCardDeviations ?? ' - '
                : stats.workControl.client
                    .percentageOfPersonnelWithIdCardDeviations}
              %
            </S.TBodyCell>
            <S.TBodyCell>
              {stats.workControl.all.percentageOfPersonnelWithIdCardDeviations}%
            </S.TBodyCell>
          </S.TBodyRow>
          <S.TBodyRow>
            <S.TBodyCell style={{ paddingLeft: 5 }}>
              <S.H6 style={{ fontWeight: 400, fontSize: 14 }}>
                Safe Construction Training saknas
              </S.H6>
            </S.TBodyCell>
            <S.TBodyCell>
              {project
                ? stats.workControl.project
                    .percentageOfPersonnelWithoutSafeConstructionTraining ??
                  ' - '
                : stats.workControl.client
                    .percentageOfPersonnelWithoutSafeConstructionTraining}
              %
            </S.TBodyCell>
            <S.TBodyCell>
              {
                stats.workControl.all
                  .percentageOfPersonnelWithoutSafeConstructionTraining
              }
              %
            </S.TBodyCell>
          </S.TBodyRow>
          <S.TBodyRow>
            <S.TBodyCell style={{ paddingLeft: 5 }}>
              <S.H6 style={{ fontWeight: 400, fontSize: 14 }}>
                Personlig säkerhetsutrustning, avvikelse mot Arbetsmiljöverkets
                föreskrifter
              </S.H6>
            </S.TBodyCell>
            <S.TBodyCell>
              {project
                ? stats.workControl.project
                    .percentageOfPersonnelWithoutSafeConstructionTraining ??
                  ' - '
                : stats.workControl.client
                    .percentageOfPersonnelWithoutSafeConstructionTraining}
              %
            </S.TBodyCell>
            <S.TBodyCell>
              {
                stats.workControl.all
                  .percentageOfPersonnelWithoutSafeConstructionTraining
              }
              %
            </S.TBodyCell>
          </S.TBodyRow>
          <S.TBodyRow>
            <S.TBodyCell style={{ paddingLeft: 5 }}>
              <S.H6 style={{ fontWeight: 400, fontSize: 14 }}>
                Avvikelser mot utstationeringslagen
              </S.H6>
            </S.TBodyCell>
            <S.TBodyCell>
              {project
                ? stats.workControl.project
                    .percentageOfPersonnelWithoutWorkPermit ?? ' - '
                : stats.workControl.client
                    .percentageOfPersonnelWithoutWorkPermit}
              %
            </S.TBodyCell>
            <S.TBodyCell>46%</S.TBodyCell>
          </S.TBodyRow>
          <S.TBodyRow>
            <S.TBodyCell style={{ paddingLeft: 5 }}>
              <S.H6 style={{ fontWeight: 400, fontSize: 14 }}>
                Förgodkända UE
              </S.H6>
            </S.TBodyCell>
            <S.TBodyCell>
              {project
                ? stats.workControl.project
                    .percentageOfAcceptedSubcontractors ?? ' - '
                : stats.workControl.client.percentageOfAcceptedSubcontractors}
              %
            </S.TBodyCell>
            <S.TBodyCell>
              {stats.workControl.all.percentageOfAcceptedSubcontractors}%
            </S.TBodyCell>
          </S.TBodyRow>
          <S.TBodyRow>
            <S.TBodyCell style={{ paddingLeft: 5 }}>
              <S.H6 style={{ fontWeight: 400, fontSize: 14 }}>
                Ej arbetstillstånd
              </S.H6>
            </S.TBodyCell>
            <S.TBodyCell>
              {project
                ? stats.workControl.project
                    .percentageOfPersonnelWithoutWorkPermit ?? ' - '
                : stats.workControl.client
                    .percentageOfPersonnelWithoutWorkPermit}
              %
            </S.TBodyCell>
            <S.TBodyCell>
              {stats.workControl.all.percentageOfPersonnelWithoutWorkPermit}%
            </S.TBodyCell>
          </S.TBodyRow>
        </S.TBody>
      </S.Table>

      <S.Margin $top="xl" />
      <S.Margin $top="l" />
      <S.Flex $width={'100%'} $justify="between" $flexDir="row" $flexGrow={1}>
        <S.Box>
          <S.H6 $align="center">Ej åtgärdade avvikelser</S.H6>
          <S.Margin $top="m" />
          <S.ColorBox>
            <S.H3 style={{ margin: 'auto' }}>
              {openAdjusted ? 0 : stats.deviations.open} st
            </S.H3>
          </S.ColorBox>
        </S.Box>
        <S.Box>
          <S.H6 $align="center">Åtgärdade avvikelser</S.H6>
          <S.Margin $top="m" />
          <S.ColorBox>
            <S.H3 style={{ margin: 'auto' }}>{stats.deviations.closed} st</S.H3>
          </S.ColorBox>
        </S.Box>
      </S.Flex>

      {openAdjusted && (
        <>
          <S.Margin $top="m" />
          <S.Warning role="alert">
            Antalet ej åtgärdade avvikelser har justerats eftersom statistik
            ännu inte har genererats för en eller flera kontroller. Be
            administratörerna att generera statistiken för att se korrekta
            siffror.
          </S.Warning>
        </>
      )}
    </S.StatsInner>
  );
};

const CountUpPreset = ({
  children,
  value,
}: {
  children: (props: RenderCounterProps) => ReactNode;
  value: number;
}) => {
  return (
    <CountUp start={0} end={value} duration={3} delay={0}>
      {children}
    </CountUp>
  );
};
